import React, { useState, useEffect, useRef } from "react";
import { Send, X } from "lucide-react";

const faqData = {
  greetings: {
    keywords: ["hi", "hello", "hey", "salam", "good morning", "good afternoon", "good evening", "greetings", "howdy", "whats up", "how are you"],
    response: "Hello! 👋 I'm Waya, WayForward IT's assistant. Great to have you here — how can I help you today?"
  },
  botName: {
    keywords: ["your name", "who are you", "what is your name", "name", "waya", "introduce yourself"],
    response: "I'm Waya — WayForward IT's digital assistant. I'm here to answer any questions about our services, team, pricing, or anything IT-related. Nice to meet you! 😊"
  },
  botPurpose: {
    keywords: ["what do you do", "what can you do", "help me", "your job", "your role", "purpose"],
    response: "I can help you with:\n• Our IT services & solutions\n• Pricing & consultations\n• Technology stack info\n• Contact & office hours\n• Anything about WayForward IT!"
  },
  howAreYou: {
    keywords: ["how are you", "how do you feel", "whats going on", "how is everything"],
    response: "I'm running at full capacity and ready to help! 🚀 What can I do for you today?"
  },
  location: {
    keywords: ["location", "where", "address", "office", "based", "situated", "place", "headquarters"],
    response: "📍 We're headquartered at:\n5900 Balcones Drive #27580\nAustin, TX 78731, USA\n\nWe serve clients globally — distance is never a barrier."
  },
  services: {
    keywords: ["services", "what", "offer", "do", "provide", "solutions", "help", "work", "capabilities"],
    response: "We offer end-to-end IT solutions:\n• Custom Software Development\n• Cloud Solutions & Migration\n• Cybersecurity Services\n• Mobile App Development\n• Data Analytics & BI\n• IT Infrastructure Management\n• Network Solutions\n• 24/7 Technical Support"
  },
  webDevelopment: {
    keywords: ["web", "website", "development", "frontend", "backend", "html", "css", "javascript", "react", "responsive"],
    response: "We build fast, secure, and scalable web applications using React, Node.js, Python, .NET, and more — fully tailored to your business needs."
  },
  appDevelopment: {
    keywords: ["app", "mobile", "android", "ios", "application", "smartphone", "tablet", "native", "cross-platform"],
    response: "We develop native iOS & Android apps, as well as cross-platform solutions using React Native and Flutter — optimized for performance and user experience."
  },
  cloudServices: {
    keywords: ["cloud", "aws", "azure", "hosting", "server", "deployment", "migration", "infrastructure"],
    response: "Our cloud team handles AWS, Azure & GCP migrations, infrastructure setup, DevOps pipelines, and 24/7 cloud monitoring to keep you running smoothly."
  },
  contact: {
    keywords: ["contact", "email", "phone", "call", "reach", "touch", "communicate", "get in touch"],
    response: "📧 wayforwardit@gmail.com\n📞 +1 (555) 123-4567\n🌐 You can also reach us through the Contact page — we respond within 24 hours!"
  },
  hours: {
    keywords: ["hours", "time", "when", "open", "available", "working", "schedule", "office hours"],
    response: "🕘 Mon–Fri: 9AM–6PM EST\n🆘 Sat–Sun: Emergency support only\n\nEmail us anytime — we'll always get back to you!"
  },
  pricing: {
    keywords: ["price", "cost", "pricing", "fee", "charge", "budget", "expensive", "cheap", "quote", "estimate"],
    response: "Pricing depends on your project's scope and complexity. We offer competitive rates and flexible payment plans. Reach out for a free quote — no strings attached!"
  },
  consultation: {
    keywords: ["consultation", "meeting", "discuss", "talk", "free", "demo", "presentation"],
    response: "We offer free consultations! We'll discuss your goals, propose tailored solutions, and give you a realistic timeline and budget. Want to schedule one?"
  },
  about: {
    keywords: ["about", "company", "who", "team", "experience", "history", "founded", "story"],
    response: "WayForward IT is a full-service technology company helping businesses transform digitally. With 15+ years of experience and 450+ projects delivered, we're built on integrity, innovation, and excellence."
  },
  technologies: {
    keywords: ["technology", "tech", "programming", "languages", "framework", "tools", "stack"],
    response: "Our tech stack includes:\n• React, Angular, Vue.js\n• Node.js, Python, PHP, .NET\n• React Native, Flutter\n• AWS, Azure, Google Cloud\n• MongoDB, PostgreSQL, MySQL"
  },
  process: {
    keywords: ["process", "workflow", "how", "steps", "methodology", "approach", "development"],
    response: "Our process:\n1. Consultation & Discovery\n2. Planning & Strategy\n3. Design & Prototyping\n4. Development & Testing\n5. Deployment & Launch\n6. Ongoing Support"
  },
  security: {
    keywords: ["security", "secure", "safe", "protection", "data", "privacy", "encryption"],
    response: "Security is core to everything we build — encryption, secure auth, regular audits, and full compliance with GDPR, HIPAA, and other standards."
  },
  whyChoose: {
    keywords: ["why choose", "why us", "benefits", "advantages", "different", "special", "unique"],
    response: "WayForward IT stands out because:\n✅ 15+ years of proven expertise\n✅ End-to-end ownership\n✅ Transparent communication\n✅ On-time delivery, always\n✅ Long-term partnerships"
  },
  thanks: {
    keywords: ["thank", "thanks", "appreciate", "grateful", "awesome", "great"],
    response: "You're welcome! Always happy to help. Anything else I can assist you with? 😊"
  },
  goodbye: {
    keywords: ["bye", "goodbye", "see you", "farewell", "exit", "quit", "later"],
    response: "Thanks for chatting! Have a great day 👋 Reach out anytime — WayForward IT is always here."
  },
  fun: {
    keywords: ["joke", "funny", "laugh", "humor", "fun"],
    response: "Why do programmers prefer dark mode? Because light attracts bugs! 😄 Anyway — any IT questions I can actually help with?"
  }
};

const quickReplies = [
  "Our Services",
  "Get a Quote",
  "Contact Info",
  "Technologies",
];

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    {
      from: "bot",
      text: "Hi there! 👋 I'm **Waya**, WayForward IT's assistant.\nAsk me anything about our services, pricing, or team.",
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    },
  ]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [showQuick, setShowQuick] = useState(true);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, typing]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 300);
  }, [open]);

  const findAnswer = (q) => {
    const lower = q.toLowerCase().trim();
    let best = null; let maxScore = 0;
    Object.values(faqData).forEach(({ keywords, response }) => {
      let score = 0;
      keywords.forEach(k => {
        if (lower.includes(k) || k.includes(lower)) score += lower === k ? 3 : 1;
      });
      if (score > maxScore) { maxScore = score; best = response; }
    });
    return best || "I'm not sure about that one — but our team can help! 📧 wayforwardit@gmail.com or use the Contact page. Anything else?";
  };

  const send = (text) => {
    const q = text || input.trim();
    if (!q) return;
    setInput("");
    setShowQuick(false);
    setMsgs(p => [...p, { from: "user", text: q, time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) }]);
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMsgs(p => [...p, { from: "bot", text: findAnswer(q), time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) }]);
    }, 900 + Math.random() * 700);
  };

  const formatText = (text) =>
    text.split("\n").map((line, i) => (
      <span key={i}>
        {line.replace(/\*\*(.*?)\*\*/g, (_, m) => m).split(/\*\*(.*?)\*\*/).map((part, j) =>
          j % 2 === 1 ? <strong key={j}>{part}</strong> : part
        )}
        {i < text.split("\n").length - 1 && <br />}
      </span>
    ));

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

        /* ── LAUNCHER BUTTON ── */
        .cb-launcher {
          position: fixed;
          bottom: 2rem;
          right: 2rem;
          z-index: 9999;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
        }

        /* "Chat with us" pill label */
        .cb-label {
          background: rgba(255,255,255,.06);
          border: 1px solid rgba(255,255,255,.12);
          backdrop-filter: blur(12px);
          color: rgba(255,255,255,.75);
          font-family: 'Inter', sans-serif;
          font-size: .7rem;
          font-weight: 600;
          letter-spacing: .06em;
          text-transform: uppercase;
          padding: .38rem .9rem;
          border-radius: 100px;
          white-space: nowrap;
          animation: labelPop .6s cubic-bezier(.34,1.56,.64,1) 2s both;
          pointer-events: none;
        }
        @keyframes labelPop {
          from { opacity: 0; transform: translateY(8px) scale(.9); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }

        /* outer pulse rings */
        .cb-ring {
          position: absolute;
          width: 100%; height: 100%;
          border-radius: 50%;
          border: 2px solid rgba(37,99,235,.5);
          animation: ringOut 2.5s ease-out infinite;
          pointer-events: none;
        }
        .cb-ring:nth-child(2) { animation-delay: .8s; }
        .cb-ring:nth-child(3) { animation-delay: 1.6s; }
        @keyframes ringOut {
          0%   { transform: scale(1);   opacity: .7; }
          100% { transform: scale(1.9); opacity: 0;  }
        }

        /* the actual circle button */
        .cb-btn {
          position: relative;
          width: 62px; height: 62px;
          border-radius: 50%;
          border: none; cursor: pointer;
          background: conic-gradient(from 180deg, #1d4ed8, #3b82f6, #2563eb, #1d4ed8);
          box-shadow: 0 0 0 3px rgba(37,99,235,.25), 0 8px 32px rgba(37,99,235,.5);
          display: flex; align-items: center; justify-content: center;
          transition: transform .3s cubic-bezier(.34,1.56,.64,1), box-shadow .3s ease;
          animation: btnSpin 8s linear infinite;
        }
        @keyframes btnSpin {
          to { filter: hue-rotate(360deg); }
        }
        .cb-btn:hover {
          transform: scale(1.12);
          box-shadow: 0 0 0 4px rgba(37,99,235,.4), 0 12px 40px rgba(37,99,235,.65);
        }
        .cb-btn-inner {
          width: 50px; height: 50px;
          border-radius: 50%;
          background: #080808;
          display: flex; align-items: center; justify-content: center;
          flex-direction: column;
          gap: 1px;
        }
        .cb-btn-icon { color: #fff; transition: transform .3s ease; }
        .cb-btn:hover .cb-btn-icon { transform: scale(1.15) rotate(-8deg); }

        /* notification dot */
        .cb-notif {
          position: absolute;
          top: 3px; right: 3px;
          width: 13px; height: 13px;
          border-radius: 50%;
          background: #ef4444;
          border: 2px solid #080808;
          animation: notifPulse 1.5s ease-in-out infinite;
        }
        @keyframes notifPulse {
          0%,100% { transform: scale(1); }
          50%      { transform: scale(1.25); }
        }

        /* ── CHAT WINDOW ── */
        .cb-window {
          position: fixed;
          bottom: 7rem;
          right: 2rem;
          z-index: 9998;
          width: 360px;
          max-height: 560px;
          display: flex;
          flex-direction: column;
          border-radius: 20px;
          overflow: hidden;
          background: #0f0f0f;
          border: 1px solid rgba(255,255,255,.09);
          box-shadow: 0 24px 80px rgba(0,0,0,.8), 0 0 0 1px rgba(37,99,235,.15);
          font-family: 'Inter', sans-serif;
          transform-origin: bottom right;
        }
        .cb-window.entering {
          animation: winOpen .4s cubic-bezier(.34,1.56,.64,1) both;
        }
        .cb-window.leaving {
          animation: winClose .25s ease-in both;
        }
        @keyframes winOpen  { from { opacity:0; transform:scale(.7) translateY(30px); } to { opacity:1; transform:scale(1) translateY(0); } }
        @keyframes winClose { from { opacity:1; transform:scale(1); } to { opacity:0; transform:scale(.7) translateY(20px); } }

        /* HEADER */
        .cb-head {
          padding: 1.1rem 1.2rem;
          background: #0a0a0a;
          border-bottom: 1px solid rgba(255,255,255,.07);
          display: flex; align-items: center; justify-content: space-between;
          flex-shrink: 0;
        }
        .cb-head-left { display: flex; align-items: center; gap: 10px; }
        .cb-avatar {
          width: 38px; height: 38px; border-radius: 50%;
          background: conic-gradient(from 180deg, #1d4ed8, #3b82f6, #2563eb);
          display: flex; align-items: center; justify-content: center;
          font-size: 1rem; flex-shrink: 0;
          box-shadow: 0 0 12px rgba(37,99,235,.4);
        }
        .cb-head-name { font-size: .9rem; font-weight: 700; color: #fff; letter-spacing: -.01em; }
        .cb-head-status {
          display: flex; align-items: center; gap: 5px;
          font-size: .7rem; color: rgba(255,255,255,.38); margin-top: 1px;
        }
        .cb-status-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: #10b981; animation: onlineBlink 2.5s ease-in-out infinite;
        }
        @keyframes onlineBlink { 0%,100%{opacity:1} 50%{opacity:.4} }
        .cb-close-btn {
          width: 28px; height: 28px; border-radius: 50%;
          background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.1);
          color: rgba(255,255,255,.5); cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: all .2s ease;
        }
        .cb-close-btn:hover { background: rgba(255,255,255,.12); color: #fff; }

        /* MESSAGES */
        .cb-msgs {
          flex: 1; overflow-y: auto; padding: 1.1rem;
          display: flex; flex-direction: column; gap: .75rem;
          scroll-behavior: smooth;
          min-height: 0;
        }
        .cb-msgs::-webkit-scrollbar { width: 4px; }
        .cb-msgs::-webkit-scrollbar-track { background: transparent; }
        .cb-msgs::-webkit-scrollbar-thumb { background: rgba(255,255,255,.08); border-radius: 4px; }

        .cb-msg { display: flex; flex-direction: column; animation: msgIn .3s ease both; }
        @keyframes msgIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        .cb-msg.user { align-items: flex-end; }
        .cb-msg.bot  { align-items: flex-start; }

        .cb-bubble {
          max-width: 80%; padding: .72rem .95rem;
          border-radius: 16px; font-size: .84rem; line-height: 1.65;
          font-weight: 400;
        }
        .cb-msg.user .cb-bubble {
          background: linear-gradient(135deg, #1d4ed8, #3b82f6);
          color: #fff; border-bottom-right-radius: 4px;
        }
        .cb-msg.bot .cb-bubble {
          background: rgba(255,255,255,.06);
          border: 1px solid rgba(255,255,255,.08);
          color: rgba(255,255,255,.82);
          border-bottom-left-radius: 4px;
        }
        .cb-time {
          font-size: .63rem; color: rgba(255,255,255,.2);
          margin-top: 3px; padding: 0 3px;
        }

        /* TYPING */
        .cb-typing { display: flex; align-items: center; gap: 6px; padding: .7rem .95rem; }
        .cb-typing-label { font-size: .72rem; color: rgba(255,255,255,.3); }
        .cb-dots { display: flex; gap: 4px; }
        .cb-dot {
          width: 5px; height: 5px; border-radius: 50%;
          background: rgba(37,99,235,.7);
          animation: dotBounce 1.2s ease-in-out infinite;
        }
        .cb-dot:nth-child(2) { animation-delay: .18s; }
        .cb-dot:nth-child(3) { animation-delay: .36s; }
        @keyframes dotBounce { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-6px)} }

        /* QUICK REPLIES */
        .cb-quick {
          padding: 0 1.1rem .85rem;
          display: flex; flex-wrap: wrap; gap: .45rem;
          flex-shrink: 0;
        }
        .cb-qr {
          background: rgba(37,99,235,.12);
          border: 1px solid rgba(37,99,235,.3);
          color: #93c5fd; border-radius: 100px;
          font-size: .73rem; font-weight: 600;
          padding: .38rem .85rem; cursor: pointer;
          transition: all .2s ease; white-space: nowrap;
          font-family: 'Inter', sans-serif;
        }
        .cb-qr:hover {
          background: rgba(37,99,235,.25); border-color: rgba(37,99,235,.55);
          color: #fff; transform: translateY(-1px);
        }

        /* INPUT */
        .cb-input-row {
          padding: .85rem 1.1rem;
          border-top: 1px solid rgba(255,255,255,.07);
          background: #0a0a0a;
          display: flex; align-items: center; gap: .6rem;
          flex-shrink: 0;
        }
        .cb-input {
          flex: 1; background: rgba(255,255,255,.05);
          border: 1px solid rgba(255,255,255,.09);
          border-radius: 10px; padding: .65rem .9rem;
          color: #fff; font-family: 'Inter', sans-serif;
          font-size: .84rem; outline: none;
          transition: border-color .2s ease;
        }
        .cb-input::placeholder { color: rgba(255,255,255,.22); }
        .cb-input:focus { border-color: rgba(37,99,235,.45); }
        .cb-send {
          width: 36px; height: 36px; border-radius: 50%;
          background: linear-gradient(135deg, #1d4ed8, #3b82f6);
          border: none; cursor: pointer; color: #fff;
          display: flex; align-items: center; justify-content: center;
          transition: all .25s ease; flex-shrink: 0;
          box-shadow: 0 4px 12px rgba(37,99,235,.35);
        }
        .cb-send:hover:not(:disabled) { transform: scale(1.1) translateY(-1px); box-shadow: 0 8px 20px rgba(37,99,235,.55); }
        .cb-send:disabled { opacity: .4; cursor: not-allowed; }

        .cb-footer-txt {
          text-align: center; font-size: .6rem;
          color: rgba(255,255,255,.15); padding: .4rem 0 .6rem;
          font-family: 'Inter', sans-serif; letter-spacing: .04em;
        }

        @media (max-width: 420px) {
          .cb-window { width: calc(100vw - 2rem); right: 1rem; bottom: 6rem; }
          .cb-launcher { right: 1rem; }
        }
      `}</style>

      {/* ── CHAT WINDOW ── */}
      {open && (
        <div className="cb-window entering">
          {/* Header */}
          <div className="cb-head">
            <div className="cb-head-left">
              <div className="cb-avatar">🤖</div>
              <div>
                <div className="cb-head-name">Waya</div>
                <div className="cb-head-status">
                  <div className="cb-status-dot" />
                  Online · WayForward IT
                </div>
              </div>
            </div>
            <button className="cb-close-btn" onClick={() => setOpen(false)}>
              <X size={14} />
            </button>
          </div>

          {/* Messages */}
          <div className="cb-msgs">
            {msgs.map((m, i) => (
              <div key={i} className={`cb-msg ${m.from}`}>
                <div className="cb-bubble">{formatText(m.text)}</div>
                <div className="cb-time">{m.time}</div>
              </div>
            ))}
            {typing && (
              <div className="cb-msg bot">
                <div className="cb-bubble cb-typing">
                  <span className="cb-typing-label">Waya is typing</span>
                  <div className="cb-dots">
                    <div className="cb-dot" /><div className="cb-dot" /><div className="cb-dot" />
                  </div>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Quick replies */}
          {showQuick && (
            <div className="cb-quick">
              {quickReplies.map(qr => (
                <button key={qr} className="cb-qr" onClick={() => send(qr)}>{qr}</button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="cb-input-row">
            <input
              ref={inputRef}
              className="cb-input"
              placeholder="Ask me anything…"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !typing && send()}
              disabled={typing}
            />
            <button className="cb-send" onClick={() => send()} disabled={typing || !input.trim()}>
              <Send size={14} />
            </button>
          </div>
          <div className="cb-footer-txt">Powered by WayForward IT</div>
        </div>
      )}

      {/* ── LAUNCHER ── */}
      <div className="cb-launcher">
        {!open && <div className="cb-label">💬 Chat with us</div>}
        <div style={{ position: "relative", width: 62, height: 62 }}>
          {!open && (
            <>
              <div className="cb-ring" />
              <div className="cb-ring" />
              <div className="cb-ring" />
            </>
          )}
          <button className="cb-btn" onClick={() => setOpen(o => !o)} aria-label="Open chat">
            <div className="cb-btn-inner">
              {open ? (
                <X size={20} className="cb-btn-icon" />
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="cb-btn-icon">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              )}
            </div>
          </button>
          {!open && <div className="cb-notif" />}
        </div>
      </div>
    </>
  );
}
